import { useEffect, useRef } from 'react';

import { CheckoutInputField, CheckoutSelectField } from 'components/CheckoutFormControls';

import { Trans } from '@lingui/macro';
import { useCheckoutContext } from 'context/CheckoutContext';
import {
    countryProfiles,
    getCountryProfile,
    normalizeCountryCode,
    normalizePostalCode,
} from 'utility/countries';

export default function CustomerForm() {
    const {
        handleFormChange: onFormChange,
        handleAddressAutocomplete: onAddressAutocomplete,
        customerForm: values = {},
    } = useCheckoutContext();
    const {
        firstName,
        lastName,
        email,
        emailConfirm,
        phone,
        address,
        city,
        province,
        country,
        postalCode,
    } = values;
    const countryProfile = getCountryProfile(country.value);

    function handleFormChange(e) {
        if (e.target.id === 'pleaseDoNotAutofillThis') {
            onFormChange('address', e.target.value);
        } else {
            onFormChange(e.target.id, e.target.value);
        }
    }

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    const autocompleteRef = useRef();
    const inputRef = useRef();
    const countryCodeRef = useRef(countryProfile.code);
    const onAddressAutocompleteRef = useRef(onAddressAutocomplete);

    countryCodeRef.current = countryProfile.code;
    onAddressAutocompleteRef.current = onAddressAutocomplete;

    useEffect(() => {
        if (window.location.origin.includes('localhost') || window.googleMapsEnabled === false) {
            return undefined;
        }

        const autocomplete = new window.google.maps.places.Autocomplete(inputRef.current, {
            componentRestrictions: { country: countryCodeRef.current },
            fields: ['address_components'],
            types: ['address'],
        });
        autocompleteRef.current = autocomplete;
        const listener = autocomplete.addListener('place_changed', async function () {
            try {
                const place = await autocomplete.getPlace();
                let streetNum = '';
                let streetName = '';
                let cityVal = '';
                let subLocality = '';
                let postalTown = '';
                let provinceVal = '';
                let countryVal = '';
                let postal = '';

                for (const component of place.address_components) {
                    const { types } = component;

                    if (types.includes('street_number')) streetNum = component.long_name;
                    if (types.includes('route')) streetName = component.long_name;
                    if (types.includes('locality')) cityVal = component.long_name;
                    if (types.includes('sublocality_level_1')) subLocality = component.long_name;
                    if (types.includes('postal_town')) postalTown = component.long_name;
                    if (types.includes('administrative_area_level_1')) provinceVal = component.short_name;
                    if (types.includes('country')) countryVal = component.short_name;
                    if (types.includes('postal_code')) postal = component.short_name;
                }

                const addressLine = `${streetNum} ${streetName}`.trim();
                const selectedCountry = normalizeCountryCode(countryVal || countryCodeRef.current);

                inputRef.current.value = addressLine;
                onAddressAutocompleteRef.current(
                    addressLine,
                    cityVal || subLocality || postalTown,
                    provinceVal,
                    selectedCountry,
                    normalizePostalCode(postal, selectedCountry),
                );
            } catch (err) {
                return;
            }
        });

        return () => {
            listener.remove();
            autocompleteRef.current = undefined;
        };
    }, []);

    useEffect(() => {
        autocompleteRef.current?.setComponentRestrictions({ country: countryProfile.code });
    }, [countryProfile.code]);

    const cityColClass = countryProfile.region.mode === 'hidden' ? 'sm:col-span-2' : '';

    return (
        <div className="flex flex-col pb-4">
            <h3 className="mt-0 text-lg font-bold text-foreground">
                <Trans>Contact Information</Trans>
            </h3>
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                <CheckoutInputField
                    id="firstName"
                    autoFocus
                    label={<Trans>First Name</Trans>}
                    value={firstName.value}
                    error={firstName.error}
                    message={firstName.message}
                    onChange={handleFormChange}
                />
                <CheckoutInputField
                    id="lastName"
                    label={<Trans>Last Name</Trans>}
                    value={lastName.value}
                    error={lastName.error}
                    message={lastName.message}
                    onChange={handleFormChange}
                />
                <CheckoutInputField
                    id="email"
                    label={<Trans>Email</Trans>}
                    type="email"
                    value={email.value}
                    error={email.error}
                    message={email.message}
                    onChange={handleFormChange}
                />
                <CheckoutInputField
                    id="emailConfirm"
                    label={<Trans>Confirm Email</Trans>}
                    type="email"
                    value={emailConfirm.value}
                    error={emailConfirm.error}
                    message={emailConfirm.message}
                    onChange={handleFormChange}
                />
                <CheckoutInputField
                    id="phone"
                    label={<Trans>Phone Number</Trans>}
                    type="tel"
                    value={phone.value}
                    error={phone.error}
                    message={phone.message}
                    onChange={handleFormChange}
                />
                <CheckoutInputField
                    id="pleaseDoNotAutofillThis"
                    label={<Trans>Address</Trans>}
                    type="text"
                    autoComplete="off"
                    inputRef={inputRef}
                    defaultValue={address.value}
                    error={address.error}
                    message={address.message}
                    onChange={handleFormChange}
                    className="sm:col-span-2"
                />
                <CheckoutInputField
                    id="city"
                    label={<Trans>City</Trans>}
                    value={city.value}
                    error={city.error}
                    message={city.message}
                    onChange={handleFormChange}
                    className={cityColClass}
                />
                <RegionField region={countryProfile.region} province={province} onChange={handleFormChange} />
                <CheckoutInputField
                    id="postalCode"
                    label={countryProfile.postalCode.label()}
                    value={postalCode.value}
                    error={postalCode.error}
                    message={postalCode.message}
                    onChange={handleFormChange}
                />
                <CheckoutSelectField
                    id="country"
                    label={<Trans>Country / Region</Trans>}
                    value={country.value}
                    error={country.error}
                    message={country.message}
                    onChange={handleFormChange}
                >
                    {countryProfiles.map((profile) => (
                        <option key={profile.code} value={profile.code}>
                            {profile.label()}
                        </option>
                    ))}
                </CheckoutSelectField>
            </div>
        </div>
    );
}

function RegionField({ region, province, onChange }) {
    if (region.mode === 'hidden') return null;

    const common = {
        id: 'province',
        label: region.label(),
        value: province.value,
        error: province.error,
        message: province.message,
        onChange,
    };

    if (region.mode === 'text') {
        return <CheckoutInputField {...common} />;
    }

    return (
        <CheckoutSelectField {...common}>
            <option value="" disabled>
                {'\u00a0'}
            </option>
            {region.options.map(([value, label]) => (
                <option key={value} value={value}>
                    {label()}
                </option>
            ))}
        </CheckoutSelectField>
    );
}
